/* global gdAlertify */
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import EnvioLoteDetalle from "./EnvioLoteDetalle";
import { BadgeEstado, BarraAvance, ESTADOS_LOTE, ESTADOS_LOTE_ABIERTOS, ORIGENES, fechaHora, mensajeError } from "./enviosUi";

const POR_PAGINA = 20;

const EnviosLotes = ({ refresco }) => {
    const [filtros, setFiltros] = useState({ origen: "", estado: "", q: "" });
    const [pagina, setPagina] = useState(1);
    const [lotes, setLotes] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [loteId, setLoteId] = useState(null);

    const cargar = useCallback(async (silencioso = false) => {
        if (!silencioso) setLoading(true);
        try {
            const { data } = await axiosInstance.get("/envios/lotes", {
                params: { ...filtros, page: pagina, limit: POR_PAGINA },
            });
            setLotes(data?.data || { items: [], total: 0 });
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudieron cargar los envíos."));
        } finally {
            setLoading(false);
        }
    }, [filtros, pagina]);

    useEffect(() => {
        if (!loteId) cargar();
    }, [cargar, loteId, refresco]);

    // Mientras haya lotes en curso, se refresca solo cada 30 s.
    const hayAbiertos = lotes.items.some((l) => ESTADOS_LOTE_ABIERTOS.includes(l.estado));
    useEffect(() => {
        if (loteId || !hayAbiertos) return undefined;
        const intervalo = setInterval(() => cargar(true), 30000);
        return () => clearInterval(intervalo);
    }, [cargar, hayAbiertos, loteId]);

    const cambiarFiltro = (campo, valor) => {
        setPagina(1);
        setFiltros((prev) => ({ ...prev, [campo]: valor }));
    };

    if (loteId) {
        return <EnvioLoteDetalle loteId={loteId} onVolver={() => setLoteId(null)} />;
    }

    const paginas = Math.max(1, Math.ceil(lotes.total / POR_PAGINA));

    return (
        <>
            <div className="row g-2 mb-3">
                <div className="col-md-3">
                    <select className="form-select" value={filtros.origen} onChange={(e) => cambiarFiltro("origen", e.target.value)}>
                        <option value="">Todos los orígenes</option>
                        {Object.entries(ORIGENES).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
                    </select>
                </div>
                <div className="col-md-3">
                    <select className="form-select" value={filtros.estado} onChange={(e) => cambiarFiltro("estado", e.target.value)}>
                        <option value="">Todos los estados</option>
                        {Object.entries(ESTADOS_LOTE).map(([valor, def]) => <option key={valor} value={valor}>{def.label}</option>)}
                    </select>
                </div>
                <div className="col-md-4">
                    <input className="form-control" placeholder="Buscar por nombre o referencia" value={filtros.q}
                        onChange={(e) => cambiarFiltro("q", e.target.value)} />
                </div>
                <div className="col-md-2 text-end">
                    <button className="btn btn-outline-secondary w-100" onClick={() => cargar()}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
            ) : lotes.items.length === 0 ? (
                <div className="alert alert-light border text-center mb-0">
                    No hay envíos todavía. Puedes hacer uno desde <strong>Probar envío</strong>.
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Envío</th>
                                    <th>Origen</th>
                                    <th>Creado</th>
                                    <th>Avance</th>
                                    <th className="text-center">Enviados</th>
                                    <th className="text-center">Leídos</th>
                                    <th className="text-center">Fallidos</th>
                                    <th className="text-center">Pendientes</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lotes.items.map((l) => (
                                    <tr key={l.id} role="button" onClick={() => setLoteId(l.id)}>
                                        <td className="text-muted">{l.id}</td>
                                        <td>
                                            <strong>{l.nombre}</strong>
                                            {l.referencia && <small className="text-muted ms-2">{l.referencia}</small>}
                                            {l.usuario_nombre && <small className="d-block text-muted">{l.usuario_nombre}</small>}
                                        </td>
                                        <td>{ORIGENES[l.origen] || l.origen}</td>
                                        <td><small>{fechaHora(l.created_at)}</small></td>
                                        <td><BarraAvance lote={l} /></td>
                                        <td className="text-center">{l.enviados}</td>
                                        <td className="text-center">{l.leidos}</td>
                                        <td className="text-center">{Number(l.fallidos) > 0 ? <span className="text-danger fw-semibold">{l.fallidos}</span> : 0}</td>
                                        <td className="text-center">{l.pendientes}</td>
                                        <td><BadgeEstado estado={l.estado} tipo="lote" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {paginas > 1 && (
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <button className="btn btn-sm btn-outline-secondary" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
                                <i className="bi bi-chevron-left"></i>
                            </button>
                            <small>Página {pagina} de {paginas}</small>
                            <button className="btn btn-sm btn-outline-secondary" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default EnviosLotes;
